<template>
  <div class="max-w-5xl mx-auto space-y-6">
    <!-- Page header -->
    <div class="flex items-center gap-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">edit</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Edit Driver</h2>
        <p class="text-sm text-gray-500">Update this driver's profile, documents and status.</p>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <form @submit.prevent class="space-y-6 lg:col-span-2">
        <!-- Basic information -->
        <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div class="flex items-center mb-5 space-x-1 text-gray-500">
            <span class="text-base material-icons-round">badge</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Basic Information</span>
          </div>

          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <label class="block">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">First Name</span>
              <input
                v-model="driver.firstName"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Jane"
              />
            </label>
            <label class="block">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Last Name</span>
              <input
                v-model="driver.lastName"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Doe"
              />
            </label>
            <label class="block">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Taxiye ID</span>
              <input
                v-model="driver.driverId"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="taxiye ID"
              />
            </label>
            <label class="block">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">City</span>
              <input
                v-model="driver.city"
                type="text"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Addis Ababa"
              />
            </label>
            <label class="block">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Email</span>
              <input
                v-model="driver.email"
                type="email"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="johndoe@gmail.com"
              />
            </label>
            <label class="block">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Contact Number</span>
              <input
                v-model="driver.phoneNumber"
                type="text"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="+251 912345678"
              />
            </label>
            <label class="block sm:col-span-2">
              <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle Type</span>
              <select
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                v-model="driver.vehicleType"
              >
                <option
                  v-for="type in vehicleTypes"
                  :value="type._id"
                  :key="`vType-${type._id}`"
                >{{ type.name }}</option>
              </select>
            </label>
          </div>

          <label class="flex items-center gap-3 mt-5 cursor-pointer">
            <input type="checkbox" class="w-5 h-5 rounded form-checkbox text-taxiye-500" v-model="driver.isEmployee" />
            <span class="text-sm text-gray-700">This driver is an employee</span>
          </label>
        </div>

        <!-- Documents -->
        <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div class="flex items-center mb-5 space-x-1 text-gray-500">
            <span class="text-base material-icons-round">description</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Documents</span>
          </div>

          <div class="flex flex-wrap gap-8">
            <photo-uploader v-model="driver.profileImage" label="Profile Picture" />
            <photo-uploader v-model="driver.businessLicense" label="Business License" />
            <photo-uploader v-model="driver.drivingLicense" label="Driver's License" />
          </div>

          <div class="mt-6">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Additional Document(s)</span>
            <div class="flex flex-wrap gap-8 mt-3">
              <photo-uploader
                v-for="(i, index) in driver.additionalDocuments"
                :key="`additional-document-${index}`"
                :value="driver.additionalDocuments[index]"
                @input="driver.additionalDocuments.splice(index, 1)"
              />
              <photo-uploader @input="(x) => driver.additionalDocuments.push(x)" />
            </div>
          </div>

          <label class="flex items-center gap-3 mt-6 cursor-pointer">
            <input
              type="checkbox"
              class="w-5 h-5 rounded form-checkbox text-taxiye-500"
              :checked="representative"
              v-model="representative"
            />
            <span class="text-sm text-gray-700">Representative (Optional)</span>
          </label>
          <div v-if="representative" class="mt-4">
            <photo-uploader v-model="driver.representationPaper" label="Representation Paper" />
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-3">
          <back-button></back-button>
          <button
            class="inline-flex items-center h-10 px-5 text-sm font-semibold text-white shadow-sm rounded-xl bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
            @click="edit"
          >
            <span class="mr-1 text-base material-icons-round">save</span>
            Save Changes
          </button>
        </div>
      </form>

      <!-- Status toggles -->
      <div class="lg:col-span-1">
        <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
          <div class="flex items-center mb-5 space-x-1 text-gray-500">
            <span class="text-base material-icons-round">tune</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Status</span>
          </div>

          <div class="flex flex-col divide-y divide-gray-100">
            <div class="flex items-center justify-between py-3">
              <span class="text-sm text-gray-700">Profile complete</span>
              <div class="relative inline-block w-16 align-middle transition duration-200 ease-in select-none">
                <input
                  type="checkbox"
                  name="toggle"
                  v-model="driver.completed"
                  id="toggle"
                  class="absolute block w-8 h-8 bg-white border-4 rounded-full appearance-none cursor-pointer toggle-checkbox"
                />
                <label
                  for="toggle"
                  class="block h-8 overflow-hidden bg-gray-300 rounded-full cursor-pointer toggle-label"
                ></label>
              </div>
            </div>
            <div class="flex items-center justify-between py-3">
              <span class="text-sm text-gray-700">Has completed payment</span>
              <div class="relative inline-block w-16 align-middle transition duration-200 ease-in select-none">
                <input
                  type="checkbox"
                  name="toggle"
                  v-model="driver.hasCompletedPayment"
                  id="toggle-payment"
                  class="absolute block w-8 h-8 bg-white border-4 rounded-full appearance-none cursor-pointer toggle-checkbox"
                />
                <label
                  for="toggle-payment"
                  class="block h-8 overflow-hidden bg-gray-300 rounded-full cursor-pointer toggle-label"
                ></label>
              </div>
            </div>
            <div class="flex items-center justify-between py-3">
              <span class="text-sm text-gray-700">Delivery driver</span>
              <div class="relative inline-block w-16 align-middle transition duration-200 ease-in select-none">
                <input
                  type="checkbox"
                  name="toggle"
                  v-model="driver.deliveryDriver"
                  id="toggle-delivery"
                  class="absolute block w-8 h-8 bg-white border-4 rounded-full appearance-none cursor-pointer toggle-checkbox"
                />
                <label
                  for="toggle-delivery"
                  class="block h-8 overflow-hidden bg-gray-300 rounded-full cursor-pointer toggle-label"
                ></label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PhotoUploader from "@/components/PhotoUploader.vue";
import BackButton from "@/components/BackButton.vue";

export default {
  components: {
    PhotoUploader,
    BackButton,
  },
  data() {
    return {
      driver: {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        profileImage: "",
        city: "",
        vehicleType: "",
        businessLicense: "",
        drivingLicense: "",
        representationPaper: "",
        additionalDocuments: [],
        completed: true,
        driverId: "",
      },
      vehicleTypes: [],
      representative: null,
    };
  },
  created() {
    this.representative = false;
    this.getVehicleTypes();
    this.getDriver();
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
  },
  methods: {
    getDriver() {
      this.$misc
        .get(`drivers/${this.id}`)
        .then((response) => {
          this.driver = response.data.driver;
          if (response.data.vehicle && response.data.vehicle.vehicleType) {
            this.driver.vehicleType = response.data.vehicle.vehicleType._id;
          }
          this.representative = response.data.driver.representationPaper
            ? true
            : false;
        })
        .catch((err) => console.log(err));
    },
    getVehicleTypes() {
      this.$misc
        .get("vehicleTypes")
        .then(({ data }) => (this.vehicleTypes = data.data))
        .catch((err) => console.log(err));
    },
    edit() {
      this.$misc
        .patch(`drivers/${this.id}`, this.driver)
        .then(() => {
          window.flash({
            type: "success",
            body: "Driver updated successfully!",
          });
          this.$router.push("/drivers");
        })
        .catch((err) => {
          if (err.response && err.response.data && err.response.data.message && err.response.data.message.includes('ValidationError')) {
            window.flash({
              type: "error",
              body: err.response.data.message.split(':').slice(1).join(':').split(',').join('<br/>'),
            });
            console.log(err);
          } else {
            window.flash({
              type: "error",
              body: "Driver couldn't be updated!",
            });
            console.log(err);
          }
        });
    },
  },
};
</script>
<style>
.toggle-checkbox:checked {
  @apply right-0;
  @apply border-green-400;
  right: 0;
  border-color: #68d391;
}
.toggle-checkbox:checked + .toggle-label {
  @apply bg-green-400;
  background-color: #68d391;
}
</style>